import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Row, Col, Input } from 'reactstrap';
import { CardTitle } from '../../../../../../components/text';
import {
  Select,
  SelectOptionType,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useValidations } from '../../../../../../hooks/useValidations';
import { useParameters } from '../../../../../../hooks/useParameters';
import { useCourseModalLoader } from './CourseModalLoader';
import RemoteSearchSelect from '../../../../../../components/form/RemoteSearchSelect';
import { COURSE_GROUP_TYPE_VALUE } from '../../../../../../types/CurriculumTypes';
import { searchCourseGroupingsForCourseAction } from '../../../../courses/actions';
import { CourseGroupingPick } from '../../../../courses/types';

const prefix = 'studyPlans.curriculum.courseModal';

interface GroupingOption extends SelectOptionType {
  raw: CourseGroupingPick;
}

function CourseModalForm() {
  const { control, watch, setValue } = useFormContext();
  const { msgValidations } = useValidations();
  const { courseTypeOptions } = useParameters();
  // The loader returns `data: undefined` until the first fetch resolves.
  // Using `data: obligatoryCourses = []` in destructuring would create a
  // brand-new `[]` on every render, breaking memo equality for any
  // useCallback/useEffect that depends on it (this is what caused the
  // "Maximum update depth exceeded" loop in useForm.ts).
  const { data: loaderData, updateQuery } = useCourseModalLoader();
  const obligatoryCourses = useMemo(() => loaderData ?? [], [loaderData]);
  const [typeValue, nameValue] = watch(['type', 'name']);
  const { t } = useTranslation();
  const lastRequestIdRef = useRef<number>(0);

  // HU 15578: course-group branch — uncontrolled local state for the
  // picked group (matches the pattern used by AddCourseGroupingModal in
  // HU 15577). Wrapping RemoteSearchSelect in a react-hook-form
  // Controller blocks the search input from receiving keystrokes.
  const [pickedGroup, setPickedGroup] = useState<CourseGroupingPick | null>(
    null,
  );

  // HU 15576: the malla type selector now offers only the per-tenant
  // course term plus the virtual "Agrupación de actividades" option —
  // the rest (Obligatorio, Optativos, Otros) was removed since UCSH
  // only composes mallas out of activities. The first entry reuses
  // the Obligatorio courseType (loaded from the API): for UCSH the
  // existing obligatorio courses ARE the activities. Its visible
  // label comes from the language-layer term (`terms:course.single`,
  // overridden to "Actividad" per tenant). The `isActivity` flag is
  // the stable discriminator the modal uses on submit to classify
  // the picked course as an activity in `legend` regardless of how
  // the term resolves.
  const courseGroupLabel = t(`${prefix}.courseGroup`);
  const courseTermLabel = t('terms:course.single');
  const typeOptions = useMemo(() => {
    const obligatorio = courseTypeOptions.find(
      (o) => String(o.label).toLowerCase() === 'obligatorio',
    );
    return [
      ...(obligatorio
        ? [
            {
              ...obligatorio,
              label: courseTermLabel,
              generalTypeId: 1,
              isActivity: true,
            },
          ]
        : []),
      {
        label: courseGroupLabel,
        value: COURSE_GROUP_TYPE_VALUE,
        isCourseGroup: true,
      },
    ];
  }, [courseTypeOptions, courseTermLabel, courseGroupLabel]);

  const resetFields = useCallback(() => {
    setValue('code', '');
    setValue('courseCode', '');
    setValue('credits', '');
    setValue('courseCredits', '');
    setValue('name', null);
    setValue('courseName', '');
  }, [setValue]);

  const fillFields = useCallback(
    (id: any) => {
      const row = obligatoryCourses.find(
        (course) => course.id === parseInt(id?.value || -1),
      );

      if (row) {
        setValue('code', row?.shortening);
        setValue('courseCode', row?.code);
        setValue('credits', row?.credits.toString());
        setValue('courseCredits', row?.credits.toString());
        setValue('courseName', row?.name);
      } else {
        resetFields();
      }
    },
    [resetFields, setValue, obligatoryCourses],
  );

  useEffect(() => {
    fillFields(nameValue);
  }, [nameValue, fillFields]);

  useEffect(() => {
    if (typeof typeValue?.value === 'number') {
      updateQuery({ courseTypeId: typeValue?.value });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeValue]);

  // HU 15578: clear the picked group whenever the user toggles away
  // from the course-group branch.
  useEffect(() => {
    if (typeValue?.isCourseGroup !== true && pickedGroup !== null) {
      setPickedGroup(null);
    }
  }, [typeValue, pickedGroup]);

  const filterCourseGroups = useCallback(
    async (searchText: string): Promise<GroupingOption[]> => {
      const requestId = ++lastRequestIdRef.current;
      try {
        const result = await searchCourseGroupingsForCourseAction({
          name: searchText,
          page: 0,
          items_per_page: 20,
        });
        if (requestId !== lastRequestIdRef.current) {
          return [];
        }
        if (!result.success) {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`${prefix}.errorOnSearch`),
          });
          return [];
        }
        const items = result.data?.items ?? [];
        return items.map((item) => ({
          value: item.id,
          label: `${item.name} (${item.code})`,
          raw: {
            id: item.id,
            name: item.name,
            code: item.code,
            school: item.school,
          },
        }));
      } catch {
        if (requestId === lastRequestIdRef.current) {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`${prefix}.errorOnSearch`),
          });
        }
        return [];
      }
    },
    [t],
  );

  const isCourseGroupBranch = typeValue?.isCourseGroup === true;
  const pickedGroupCode = pickedGroup?.code ?? '';
  const pickedGroupSchool = pickedGroup?.school?.name ?? '';

  // Sync the picked group into form state so onSubmit can read it.
  useEffect(() => {
    setValue(
      'group',
      pickedGroup
        ? {
            value: pickedGroup.id,
            label: `${pickedGroup.name} (${pickedGroup.code})`,
            raw: pickedGroup,
          }
        : null,
    );
  }, [pickedGroup, setValue]);

  return (
    <Row>
      <Col>
        <div className="text-center">
          <CardTitle
            children={t(`${prefix}.title`, {
              type: 'Asignatura',
            })}
          />
        </div>
        <Row className="mt-4 mt-md-5">
          <Col xs={12}>
            <Select
              name="type"
              label={t(`${prefix}.type`)}
              options={typeOptions}
              control={control}
              rules={{ required: msgValidations.required }}
            />
          </Col>
        </Row>

        {/* Branch: existing required-courses flow */}
        {typeValue?.generalTypeId === 1 && (
          <Row>
            <Col xs={12} md>
              <Select
                name="name"
                label={t(`${prefix}.name`, {
                  type: 'Asignatura',
                })}
                options={obligatoryCourses.map((course) => ({
                  label: course.name,
                  value: course.id,
                  shortening: course.shortening,
                }))}
                control={control}
                rules={{ required: msgValidations.required }}
              />
              <Input type="hidden" name="courseName" control={control} />
            </Col>
          </Row>
        )}

        {/* Branch: course-group (HU 15578) */}
        {isCourseGroupBranch && (
          <>
            <Row className="mt-3">
              <Col xs={12}>
                <RemoteSearchSelect
                  name="groupSearch"
                  label={t(`${prefix}.group`)}
                  filterOptions={filterCourseGroups}
                  isClearable
                  onChange={(opt) =>
                    setPickedGroup((opt as GroupingOption | null)?.raw ?? null)
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <TextInput
                  name="groupCode"
                  label={t(`${prefix}.code`)}
                  value={pickedGroupCode}
                  onChange={() => {}}
                  disabled
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <TextInput
                  name="groupSchool"
                  label={t(`${prefix}.school`)}
                  value={pickedGroupSchool}
                  onChange={() => {}}
                  disabled
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <TextInput
                  name="groupCredits"
                  label={t(`${prefix}.credits`)}
                  control={control}
                  rules={{
                    required: msgValidations.required,
                    pattern: {
                      value: /^[0-9]+$/,
                      message: t('common.validations.invalidIntNumeric'),
                    },
                  }}
                />
              </Col>
            </Row>
          </>
        )}

        {!isCourseGroupBranch && (
          <Row>
            {typeValue?.generalTypeId === 1 && (
              <Col xs={12}>
                <TextInput
                  name="code"
                  label={t(`${prefix}.code`)}
                  control={control}
                  disabled
                  rules={{
                    required: msgValidations.required,
                  }}
                />
                <Input type="hidden" name="courseCode" control={control} />
              </Col>
            )}
            <Col xs={12}>
              <TextInput
                name="credits"
                label={t(`${prefix}.credits`)}
                control={control}
                disabled={typeValue?.generalTypeId === 1}
                rules={{
                  required: msgValidations.required,
                  maxLength: {
                    value: 50,
                    message: t('common.validations.maxLength', {
                      length: 50,
                    }),
                  },
                  pattern: {
                    value: /^[0-9]*$/,
                    message: t('common.validations.invalidIntNumeric'),
                  },
                }}
              />
              <Input type="hidden" name="courseCredits" control={control} />
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
}

export default CourseModalForm;
